import React, { memo, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { App as AntApp, ConfigProvider, Spin } from 'antd'
import { IntlProvider } from 'react-intl'
import { useSelector } from 'react-redux'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import * as Sentry from '@sentry/react'
import qs from 'qs'
import pickBy from 'lodash/pickBy'
import identity from 'lodash/identity'
import { ClientContext, useManualQuery } from 'graphql-hooks'
import dayjs from 'dayjs'
import { getCompanyAndUserInfo, getUserActionFlow, getUserHelpInfo } from '../../graphql/custom-queries'
import * as logger from '../../services/logger'

import { identify, identifyCompany, identifySingleUser, page } from '../../services/analytics/analytics'
import { logout } from '../../services/auth/logout-handler'
import Api from '@vacationtracker/shared/services/api'

import { OnboardingProvider } from '../../context/onboardingContext'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { setFeatureFlags, selectFeatureFlagsSlice } from '../../store/feature-flags-slice'
import { isMicrosoftPayment, setAuthCompany } from '../../store/auth-company-slice'
import { selectUserIdSlice } from '../../store/user-id-slice'
import { selectLocaleSlice, setLocale } from '../../store/locale-slice'
import { selectAuthUserSlice, setAuthUser } from '../../store/auth-user-slice'
import { setRechargeType, setLoading } from '../../store/recharge-type-slice'
import { setOnboardingActions, hideOnboardingFlow } from '../../store/onboarding-slice'
import { selectAuthStateSlice, setAuthState } from '../../store/auth-state-slice'

import MainApp from './MainApp'
import Notifications from '../Notifications'
import CompanyUpdatesNotifications from '../CompanyUpdatesNotifications'
import Signin from '../signin'
import Signup from '../signup'
import SlackPostInstallation from '../slack-post-installation'
import ExternalConnect from '../external-connect'
import MicrosoftSaaSCreateCompany from '../microsoft-saas-create-company'
import SignInAsUser from '../sign-in-as-user'
import UserSignup from '../user-signup'
import OAuthAuthorize from '../oauth-authorize'
import AppLocale, { loadLanguage, ILocaleEntry } from '@vacationtracker/shared/i18n/language-provider'
import { updateGraphQlAuthHeaderAndSubscription } from '../../util/update-graphql-header-and-subscription'

import SubscriptionPage from '../../routes/SubscriptionPage'
import MicrosoftSubscriptionPage from '../../routes/MicrosoftSubscriptionPage'
import OnboardingWizard from '../../routes/OnboardingWizard'
import { SeoTags } from '../../components/seo-tags'

import { IGetCompanyAndUserInfo, IGetUserActionFlowData, IGetUserHelpInfoData } from '../../types/custom-queries'
import { IUserInfo } from '../../types/users'
import { wait } from '@vacationtracker/shared/functions/wait'
import { ICompanyInfo, SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { availableLanguages, getSafeLocale } from '@vacationtracker/shared/i18n'
import { FrontendUrls } from '../../types/urls'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { setHelpInfoActions } from '../../store/help-info-slice'
import WhatsNewNotifications from '../WhatsNewNotifications'
import GooglePermissions from '../google-permissions'
import { UserRole } from '@vacationtracker/shared/types/user'
import SlackInstallBot from '../slack-install-bot'
import { ThemeContext } from '../../context/themeContext'
import { lightThemeToken, darkThemeToken, DARK_THEME } from '@vacationtracker/shared/constants/ThemeSetting'
import { AuthStateEnum } from '@vacationtracker/shared/types/auth-state'
import { getCurrentUser, IAuthUser } from '@vacationtracker/shared/services/auth'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import CreateCompanyA from '../signup/create-company-page'
import { attachCrispTrialTriggerResponseListener, canRoleOpenSupportChat, hideChat, setCrispSessionData } from '../../util/set-crisp-session-info'
import { attachCrispPriorityListeners } from '../../util/crisp-priority'
import { collectRememberedLoginStorage, getRememberedLogin } from '../../util/remembered-login'
import { collectCompleteTrialRequestStorage } from '../../components/try-complete-request/request-storage'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any

const UDID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}/i

const App = (): React.ReactElement => {
  const { locale } = useAppSelector(selectLocaleSlice)
  const { userId } = useAppSelector(selectUserIdSlice)
  const { isSigningUp, authUser } = useAppSelector(selectAuthUserSlice)
  const { authState } = useAppSelector(selectAuthStateSlice)
  const { isLoadingFeatureFlags } = useAppSelector(selectFeatureFlagsSlice)
  const isMicrosoftBillingPayment = useSelector(isMicrosoftPayment)
  const { theme } = useContext(ThemeContext)
  const gqlClient = useContext(ClientContext)

  const location = useLocation()
  const history = useHistory()
  const match = useRouteMatch()
  const dispatch = useAppDispatch()
  const [user, setUser] = useState<IUserInfo>()
  const [currentAppLocale, setCurrentApplocale] = useState<ILocaleEntry>(AppLocale[locale.locale])
  const [isLoadingLocale, setIsLoadingLocale] = useState(false)
  const [goToDashboardSection, setGoToDashboardSection] = useState<boolean>(false)
  const [themeToken, setThemeToken] = useState(theme && theme === DARK_THEME ? darkThemeToken : lightThemeToken)

  const aiAssistedOnboardingEnabled = useFeatureFlagEnabled(FeatureFlagEnum.aiAssistedOnboarding)

  // Derive the landing URL from the Redux auth user, the same source PrivateRoute gates on.
  // A live role change (CompanyUpdatesNotifications.refetchAuthUser) only updates Redux, not the
  // local `user` state - reading local `user` here let the landing redirect disagree with the
  // route gate and ping-pong through "/", flooding analytics with $pageview events.
  const dashboardUrl = useMemo(() => {
    return authUser?.role === 'User' ? FrontendUrls.userDashboard : FrontendUrls.home
  }, [authUser?.role])

  const abortControllerRef = useRef<AbortController>(new AbortController())

  const [getUserActionFlowQuery] = useManualQuery<IGetUserActionFlowData, { type: 'dashboard' }>(getUserActionFlow)
  const [getUserHelpInfoQuery] = useManualQuery<IGetUserHelpInfoData>(getUserHelpInfo)
  const [getCompanyAndUserInfoQuery] = useManualQuery<IGetCompanyAndUserInfo, { userId: string }>(getCompanyAndUserInfo)

  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true })

  useEffect(() => {
    page(location.pathname.replace(UDID_REGEX, 'ID'))
  }, [location.pathname])

  useEffect(() => {
    if (theme === DARK_THEME) {
      setThemeToken(darkThemeToken)
    } else {
      setThemeToken(lightThemeToken)
    }
  }, [theme])

  useEffect(() => {
    abortControllerRef.current = new AbortController()
    // Include the Crisp code here, without the <script></script> tags
    const script = document.createElement('script')
    if (process.env.REACT_APP_HIDE_CRISP_CHAT !== 'true') {
      window.$crisp = []
      window.$crisp.push(['safe', true])
      window.$crisp.push(['do', 'overlay:close'])
      window.CRISP_WEBSITE_ID = 'b0a287f7-571c-45e0-92bc-3c30d8abc2ea'
      script.src = 'https://client.crisp.chat/l.js'
      script.async = true
      document.body.appendChild(script)
      attachCrispTrialTriggerResponseListener()
      attachCrispPriorityListeners()
    }
    return () => {
      if (process.env.REACT_APP_HIDE_CRISP_CHAT !== 'true') {
        document.body.removeChild(script)
      }
      abortControllerRef.current?.abort()
    }
  }, [])

  const updateHeadersAndGetCompanyAndUser = async () => {
    try {
      if (gqlClient !== null) {
        await updateGraphQlAuthHeaderAndSubscription({
          graphQlClient: gqlClient,
        })
        await getCompanyAndUser(userId, abortControllerRef.current)
      }
    } catch (error) {
      // Swallow the error
      logger.error('ERROR updateHeadersAndGetCompanyAndUser', error)
    }
  }

  useEffect(() => {
    if (userId && userId !== '') {
      abortControllerRef.current?.abort()
      abortControllerRef.current = new AbortController()
      updateHeadersAndGetCompanyAndUser()
    }
    return () => {
      // Cancel timeouts in the `getCompanyAndUser` function
      abortControllerRef.current?.abort()
    }
  }, [userId])

  useEffect(() => {
    let isMounted = true

    const updateLocale = async () => {
      setIsLoadingLocale(true)
      try {
        const loadedLocale = await loadLanguage(locale.locale)
        if (isMounted) {
          setCurrentApplocale(loadedLocale)
          dayjs.locale(getSafeLocale(locale.locale))
        }
      } finally {
        if (isMounted) {
          setIsLoadingLocale(false)
        }
      }
    }

    updateLocale()

    return () => {
      isMounted = false
    }
  }, [locale.locale])

  /**
   * Where a signed-out visitor on an unrecognised route belongs. Someone we have seen before
   * is coming back to an account, not creating one, so the remembered marker sends them to
   * sign-in. Gated on the flag because only the new sign-in reads that marker.
   */
  const signedOutLandingUrl = (): FrontendUrls => {
    const shouldUseNewFlow = process.env.REACT_APP_USE_NEW_SIGNIN_SIGNUP === 'true'
    return shouldUseNewFlow && getRememberedLogin() ? FrontendUrls.signin : FrontendUrls.signup
  }

  useEffect(() => {
    if (queryParams?.lang) {
      dispatch(setLocale(availableLanguages[queryParams.lang as LocaleEnum]))
    }
    const locationPathName = location?.pathname
    if (queryParams?.forceLogin === 'true') {
      const redirectPath = queryParams.redirect || ''
      delete queryParams.forceLogin

      logout({
        history: history,
        redirectRoute: `${FrontendUrls.signin}?${qs.stringify({ ...queryParams, redirect: redirectPath })}`,
        reduxDispatch: dispatch,
        userId: userId,
      })
    }

    if (locationPathName === FrontendUrls.googlePermissions) {
      return
    }

    if (locationPathName.includes('/app/') && !userId && !localStorage.getItem('userId')) {
      // Read before the clear, otherwise the marker is lost on exactly the visit that
      // needs it: a returning user opening a deep link with no session.
      const preservedEntries = [...collectRememberedLoginStorage(), ...collectCompleteTrialRequestStorage()]
      localStorage.clear()
      sessionStorage.clear()
      preservedEntries.forEach(([key, value]) => localStorage.setItem(key, value))
      dispatch(setAuthState(AuthStateEnum.signIn))
    }

    if (authState === AuthStateEnum.signUp) {
      if ([FrontendUrls.kats].includes(locationPathName as FrontendUrls)) {
        history.push(locationPathName)
        return
      }
    }

    if (authState === AuthStateEnum.signIn) {
      if (locationPathName.includes(FrontendUrls.externalConnect)) {
        return
      }
      if ([FrontendUrls.kats].includes(locationPathName as FrontendUrls)) {
        history.push(locationPathName)
        return
      }
      if ([FrontendUrls.googlePermissions].includes(locationPathName as FrontendUrls)) {
        history.push(locationPathName)
        return
      }
      if (locationPathName.includes(FrontendUrls.oauthAuthorize)) {
        return
      }
      if (locationPathName.includes(FrontendUrls.signin)) {
        history.push(FrontendUrls.signin + location.search)
        return
      }
      if (
        !locationPathName.includes(FrontendUrls.signup) &&
        !locationPathName.includes(FrontendUrls.slackPostInstall) &&
        !locationPathName.includes(FrontendUrls.installSlackBot) &&
        !locationPathName.includes(FrontendUrls.microsoftSaasCreateCompany)
      ) {
        history.push(signedOutLandingUrl())
        return
      }
    }

    if (authState === AuthStateEnum.signedIn) {
      if (['/', FrontendUrls.kats, FrontendUrls.googlePermissions].includes(locationPathName)) {
        if (isLoadingFeatureFlags) return
        history.push(`${dashboardUrl}${location.search}`)
        return
      }
    }

    if (authState === AuthStateEnum.loading && locationPathName === FrontendUrls.signup && localStorage.getItem('userId')) {
      if (isLoadingFeatureFlags) return
      setGoToDashboardSection(true)
      history.push(`${dashboardUrl}${location.search}`)
      return
    }

    if (authState === AuthStateEnum.signUp) {
      if (localStorage.getItem('userId') && goToDashboardSection) {
        if (isLoadingFeatureFlags) return
        history.push(`${dashboardUrl}${location.search}`)
        return
      }
      if (locationPathName === FrontendUrls.microsoftSaasCreateCompany) {
        history.push(FrontendUrls.signup)
        return
      }
      if (
        !locationPathName.includes(FrontendUrls.signup) &&
        !locationPathName.includes(FrontendUrls.slackPostInstall) &&
        !locationPathName.includes(FrontendUrls.installSlackBot) &&
        !locationPathName.includes(FrontendUrls.signin) && // For redirects
        !locationPathName.includes(FrontendUrls.oauthAuthorize) &&
        !locationPathName.includes(FrontendUrls.microsoftSaasCreateCompany)
      ) {
        history.push(signedOutLandingUrl())
        return
      }
    }
  }, [authState, location.pathname, location.search, aiAssistedOnboardingEnabled, isLoadingFeatureFlags, dashboardUrl])

  const getFeatureFlags = async (role: UserRole, pathname) => {
    try {
      const response = await Api.get(`/core/status?role=${role.toLowerCase()}&url=${pathname}`, { withCredentials: true })
      const userIdInLocalStorage = localStorage.getItem('userId')
      if (userIdInLocalStorage && userIdInLocalStorage !== response.userId) {
        logout({
          history: history,
          reduxDispatch: dispatch,
        })
      }
      dispatch(setFeatureFlags(response.enabledFeatures))
    } catch (error) {
      logger.error('ERROR', error)
      dispatch(setFeatureFlags([]))
    }
  }

  const getOnboardingChecklist = async () => {
    try {
      const response = await getUserActionFlowQuery({
        variables: {
          type: 'dashboard',
        },
        fetchOptionsOverrides: {
          signal: abortControllerRef.current?.signal,
        },
      })
      if (!response.data || response.error) throw response.error
      if (!response.data.getUserActionFlow) {
        dispatch(hideOnboardingFlow())
        return
      }
      const actions = pickBy(response.data.getUserActionFlow.actions || {}, identity)
      response.data.getUserActionFlow.actions = actions
      dispatch(setOnboardingActions(response.data.getUserActionFlow || {}))
    } catch (error) {
      logger.error('ERROR', error)
    }
  }

  const getHelpInfoForUser = async () => {
    try {
      const response = await getUserHelpInfoQuery({
        fetchOptionsOverrides: {
          signal: abortControllerRef.current?.signal,
        },
      })
      if (!response.data || response.error) throw response.error
      dispatch(setHelpInfoActions(response.data.getUserHelpInfo || {}))
    } catch (error) {
      logger.error('ERROR', error)
    }
  }

  let retryCrispSessionUpdate = 0
  const setCrispSessionInfoForLoggedInUsers = (companyInfo: ICompanyInfo, userInfo: IUserInfo): void => {
    try {
      if (!window.$crisp) {
        if (retryCrispSessionUpdate < 5) {
          retryCrispSessionUpdate++
          setTimeout(() => {
            setCrispSessionInfoForLoggedInUsers(companyInfo, userInfo)
          }, 20000)
        }
        return
      }

      // Support chat is for people who manage leave - admins, location admins and approvers
      // (see canRoleOpenSupportChat and its REACT_APP_CRISP_FOR_ALL_ROLES kill-switch); on
      // Freemium it stays admin-only either way.
      const canSeeChat = canRoleOpenSupportChat({ role: userInfo.role, isApprover: userInfo.approverTo.length > 0 })
      if (!canSeeChat || (companyInfo.plan === SubscriptionPlanEnum.freemium && userInfo.role !== 'Admin')) {
        hideChat()
        return
      }

      // Set Crisp segments, i.e., "chat," "support," "dashboard," "slack," "core-plan," and "admin"
      const segments = ['chat', 'support', 'dashboard']
      segments.push(companyInfo.platform === 'microsoft' ? 'teams' : companyInfo.platform)
      if (companyInfo.plan) {
        segments.push(companyInfo.plan.toLowerCase() + '-plan')
      }

      if (userInfo.role) {
        segments.push(userInfo.role.toLowerCase())
      }
      window.$crisp.push(['set', 'session:segments', [segments, true]])

      // Set user name and company (user data)
      window.$crisp.push(['set', 'user:nickname', userInfo.name])
      window.$crisp.push(['set', 'user:company', companyInfo.name])

      // Set session data (company Id, user Id, user role and if the user is approver)
      setCrispSessionData({
        company_id: companyInfo.id,
        user_id: userInfo.id,
        role: userInfo.role,
        locale: userInfo.locale ?? 'en',
        is_approver: userInfo.approverTo.length > 0,
        subscription_status: companyInfo.subscriptionStatus,
        plan: companyInfo.plan ?? '',
        platform: companyInfo.platform,
        payment_processor: companyInfo.billing?.paymentProcessor ?? '',
      })
    } catch (err) {
      // Swallow errors
      setCrispSessionData({ error: err.message })
      logger.error(err)
    }
  }

  let numberOfRetry = 0
  const getCompanyAndUser = async (id: string, abortController?: AbortController) => {
    try {
      const response = await getCompanyAndUserInfoQuery({
        variables: {
          userId: id,
        },
        fetchOptionsOverrides: {
          signal: abortControllerRef.current?.signal,
        },
      })
      if (!response.data || response.error) throw response.error
      if (response.data.getCompany && response.data.getUser && response.data.getUser.name) {
        if (response.data.getUser.status !== 'ACTIVE') {
          logout({
            history,
            reduxDispatch: dispatch,
          })
        }

        // We do not want to wait for the logs to be initialized because, in some situations,
        // the firewall blocks the CW logs, but instead of giving an error, it keeps the connection until the timeout.
        // When this happens, the dashboard is stuck for these users.
        logger.init()

        const companyData = response.data.getCompany
        dispatch(setAuthCompany(companyData))
        const userData = response.data.getUser
        const hasLocationAdminRole = userData.roleAssignments?.some((a) => a.roleId === 'LOCATION_ADMIN')
        if (hasLocationAdminRole && userData.role !== 'Admin') {
          userData.role = 'LocationAdmin'
        }
        dispatch(setAuthUser(userData))

        // Extract and set recharge type from user data
        if (userData.rechargeType) {
          dispatch(
            setRechargeType({
              rechargeType: userData.rechargeType,
              completedAt: userData.rechargeTypeCompletedAt || new Date().toISOString(),
            })
          )
        } else {
          dispatch(setLoading(false))
        }

        const role: UserRole = userData.role === 'Admin' ? 'Admin' : userData.approverTo.length > 0 ? 'Approver' : 'User'

        if (queryParams?.lang && LocaleEnum[queryParams?.lang as string]) {
          dispatch(setLocale(availableLanguages[queryParams.lang as LocaleEnum]))
          try {
            let body = {}
            if (userData.role === 'Admin') {
              body = {
                eventType: 'USER_UPDATED',
                eventGroup: 'USER',
                userId: userData.id,
                name: userData.name,
                locale: queryParams?.lang,
                email: userData.email,
                platform: userData.platform,
                startDate: userData.startDate,
                isAdmin: userData.role === 'Admin',
                isNameLocked: userData?.isNameLocked,
                status: userData.status,
              }
            } else {
              body = {
                eventType: 'USER_UPDATED',
                eventGroup: 'USER',
                userId: userData.id,
                name: userData.name,
                locale: queryParams?.lang,
              }
            }
            delete queryParams.lang
            history.replace({
              search: qs.stringify(queryParams),
            })
            await Api.post('/core/event', body)
          } catch (error) {
            Sentry.captureException(error)
          }
        } else if (userData.locale) {
          dispatch(setLocale(availableLanguages[userData.locale]))
        }

        getOnboardingChecklist()
        getFeatureFlags(role, history.location.pathname)
        getHelpInfoForUser()

        setUser(userData)
        Sentry.setUser({
          id: userData.id,
          companyId: companyData.id,
        })
        if (userData?.id) {
          identify(userData.id, {
            name: userData.name,
            role: userData.role,
            subscriptionStatus: companyData?.subscriptionStatus || '',
            trialPeriod: companyData?.trialPeriod || '',
            companyId: companyData?.id,
          })
          identifySingleUser(
            userData.id,
            {
              name: userData.name,
              role: userData.role,
            },
            companyData?.id
          )
          identifyCompany(companyData.id, {
            name: companyData.name,
            subscriptionStatus: companyData.subscriptionStatus,
            trialPeriod: companyData.trialPeriod,
            platform: companyData.platform,
            plan: companyData.plan,
            organizationId: companyData.organizationId,
          })
        }
        setCrispSessionInfoForLoggedInUsers(companyData, userData)
      } else {
        throw new Error('No current user, retry')
      }
    } catch (error) {
      if (error.fetchError?.name === 'AbortError') {
        return
      }
      let currentUser: IAuthUser | null = null
      try {
        // This can fail, so we have try-catch inside catch
        currentUser = await getCurrentUser()
      } catch (error) {
        // Swallow the error
      }
      if (numberOfRetry >= 10) {
        // Log only before the logout
        logger.warning('ERROR GET COMPANY AND USER (app)', error, numberOfRetry)
        logout({
          history: history,
          reduxDispatch: dispatch,
        })
      } else if (
        currentUser ||
        ![FrontendUrls.signin, FrontendUrls.signup].includes(location.pathname as FrontendUrls) ||
        location.pathname.includes(FrontendUrls.externalConnect)
      ) {
        numberOfRetry++
        await wait(200 * numberOfRetry, abortController)
        return await getCompanyAndUser(id, abortController)
      }
    }
  }

  const RedirectWithParams = ({ to, from }) => {
    return <Route path={from} render={() => <Redirect to={`${to}${location.search}`} />} />
  }

  return (
    <ConfigProvider locale={currentAppLocale.antd} theme={themeToken}>
      <AntApp notification={{ stack: { threshold: 1 }, maxCount: 3 }}>
        <IntlProvider key={currentAppLocale.locale} locale={currentAppLocale.locale} messages={currentAppLocale.messages}>
          {isLoadingLocale && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999,
              }}
            >
              <Spin size="large" />
            </div>
          )}
          <SeoTags title="app.meta.title" description="app.meta.description" />
          <Switch>
            <RedirectWithParams from={FrontendUrls.connect} to={FrontendUrls.signup} />
            <Route exact path={FrontendUrls.signin} render={(routerProps) => <Signin {...routerProps} />} />
            <Route exact path={FrontendUrls.signup} render={(routerProps) => <Signup {...routerProps} />} />
            <Route exact path={FrontendUrls.oauthAuthorize} render={(routerProps) => <OAuthAuthorize {...routerProps} />} />
            <Route exact path={`${FrontendUrls.signup}${FrontendUrls.companyDetails}`} render={(routerProps) => <CreateCompanyA {...routerProps} />} />
            <Route exact path={`${FrontendUrls.signup}${FrontendUrls.personalInfo}`} render={(routerProps) => <CreateCompanyA {...routerProps} />} />
            <Route exact path={`${FrontendUrls.signup}${FrontendUrls.selectPlan}`} render={(routerProps) => <CreateCompanyA {...routerProps} />} />
            <Route exact path={FrontendUrls.slackPostInstall} render={() => <SlackPostInstallation />} />
            <Route exact path={`${FrontendUrls.installSlackBot}/:teamId`} render={(routerProps) => <SlackInstallBot {...routerProps} />} />
            <Route exact path={`${FrontendUrls.signup}/user/:code`} render={(routerProps) => <UserSignup {...routerProps} />} />
            <Route exact path={`${FrontendUrls.connect}/campaign`} render={(routerProps) => <Signin {...routerProps} />} />
            <Route exact path={`${FrontendUrls.externalConnect}/:code`} render={(routerProps) => <ExternalConnect {...routerProps} />} />
            <Route exact path={FrontendUrls.microsoftSaasCreateCompanyWelcome} render={() => <MicrosoftSaaSCreateCompany />} />
            <Route exact path={FrontendUrls.microsoftSaasCreateCompanyDetails} render={() => <MicrosoftSaaSCreateCompany />} />
            <Route exact path={FrontendUrls.microsoftSaasCreateCompanyAssignLicenses} render={() => <MicrosoftSaaSCreateCompany />} />
            <Route exact path={FrontendUrls.microsoftSaasCreateCompanySetupBot} render={() => <MicrosoftSaaSCreateCompany />} />
            <Route exact path={FrontendUrls.kats} render={(routerProps) => <SignInAsUser {...routerProps} />} />
            <Route exact path={FrontendUrls.googlePermissions} render={(routerProps) => <GooglePermissions {...routerProps} />} />
            {user && (
              <>
                <Notifications />
                <CompanyUpdatesNotifications />
                <WhatsNewNotifications />
                {isMicrosoftBillingPayment ? (
                  <Route path={FrontendUrls.subscription} render={() => <MicrosoftSubscriptionPage />} />
                ) : (
                  <Route path={FrontendUrls.subscription} render={() => <SubscriptionPage />} />
                )}
                <Route exact path={FrontendUrls.setup} render={() => <OnboardingWizard />} />
                {location.pathname !== FrontendUrls.subscription && location.pathname !== FrontendUrls.setup && (
                  <OnboardingProvider history={history} user={user}>
                    <Route path={`${match.url}`} render={() => <MainApp user={user} />} />
                  </OnboardingProvider>
                )}
              </>
            )}
          </Switch>
        </IntlProvider>
      </AntApp>
    </ConfigProvider>
  )
}

export default memo(App)
